/**
 * @file ProjectDeadlineChip.tsx
 * @description Slice 69 — countdown pill shown next to project list rows
 *   when the project has a `desired_deadline` and is still in-flight.
 *   Hidden for completed / cancelled (deadline is moot — the project
 *   already reached its terminal state) and for projects with no
 *   deadline set. Colors escalate as the date approaches:
 *   gray (>7 days out) → amber (≤7 days) → red (≤1 day or overdue).
 *   Mirrors the slice-59 QuoteExpiryChip pattern intentionally so the
 *   visual language stays consistent across countdown UIs.
 * @module Components
 * @security Pure presentation; `desired_deadline` is a date-only string
 *   already validated server-side. Local-timezone math means "1 day"
 *   reads consistently throughout the day.
 */

import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { daysUntil } from '../../lib/date-helpers';
import type { ProjectStatus } from '../../lib/projects-api';

interface Props {
  status: ProjectStatus;
  desiredDeadline: string | null;
}

const TERMINAL: ReadonlySet<ProjectStatus> = new Set(['completed', 'cancelled']);

export function ProjectDeadlineChip({ status, desiredDeadline }: Props): JSX.Element | null {
  const { t } = useTranslation();
  if (desiredDeadline === null) return null;
  if (TERMINAL.has(status)) return null;
  const days = daysUntil(desiredDeadline);
  if (days === null) return null;
  return <span className={`chip ${classFor(days)}`}>{labelFor(days, t)}</span>;
}

/** Pure — exported for tests. The thresholds here are the contract
 *  between the chip color and the chip copy; the test pins both. */
export function classFor(days: number): string {
  if (days < 0 || days <= 1) return 'bg-red-50 text-red-700';
  if (days <= 7) return 'bg-amber-50 text-amber-700';
  return 'bg-slate-100 text-slate-600';
}

function labelFor(days: number, t: (k: string, opts?: { count: number }) => string): string {
  if (days < 0) return t('project.deadline_chip.overdue', { count: -days });
  if (days === 0) return t('project.deadline_chip.today');
  return t('project.deadline_chip.in_n_days', { count: days });
}
